import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { BonusPointsPanel } from '@/components/BonusPointsPanel'
import { ChoresManagementView } from '@/components/parent/ChoresManagementView'
import { ParentChoreManager } from '@/components/ParentChoreManager'
import { EnhancedDeductionsManager } from '@/components/EnhancedDeductionsManager'
import { KidsDashboard } from '@/components/KidsDashboard'
import { KidChoresDesktop } from '@/components/KidChoresDesktop'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { ClipboardList, Library, MinusCircle } from 'lucide-react'

/**
 * Chores page - Shows parent chores management or kid chores dashboard
 * Route: /dashboard/chores
 */
type Tab = 'assignments' | 'library' | 'deductions'
const TABS: Tab[] = ['assignments', 'library', 'deductions']
const isTab = (t: string | null): t is Tab => !!t && (TABS as string[]).includes(t)

export const ChoresPage: React.FC = () => {
  const { user } = useAuth()
  // The tab lives in the URL (?tab=library) so links such as "Go to Chore
  // Library" and the back button land on the right one.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: Tab = isTab(tabParam) ? tabParam : 'assignments'
  const setActiveTab = (tab: Tab) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'assignments') next.delete('tab')
    else next.set('tab', tab)
    setSearchParams(next)
  }
  // Remount the deductions list after a bonus so its point totals refresh.
  const [pointsKey, setPointsKey] = useState(0)
  const isDesktop = useMediaQuery('(min-width: 768px)')

  if (!user?.is_parent) {
    // Kids get responsive view: desktop chores list or mobile dashboard
    return isDesktop ? <KidChoresDesktop /> : <KidsDashboard />
  }

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('assignments')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'assignments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              Assignments
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'library'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Library className="w-5 h-5" />
              Chore Library
            </button>
            <button
              onClick={() => setActiveTab('deductions')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'deductions'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MinusCircle className="w-5 h-5" />
              Points
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'assignments' && <ChoresManagementView />}
        {activeTab === 'library' && <ParentChoreManager />}
        {activeTab === 'deductions' && (
          <div className="space-y-6">
            <BonusPointsPanel onAwarded={() => setPointsKey(k => k + 1)} />
            <EnhancedDeductionsManager key={pointsKey} />
          </div>
        )}
      </div>
    </div>
  )
}
